/**
 * MarketingShell.jsx — parent wrapper for the founder's Marketing area.
 *
 * Renders the Marketplace · Metrics · Brand Brain top-tabs (only when on
 * top-level marketing pages — engagement workspace and agency profile
 * pages hide the tab bar to maximise canvas).
 */
import React from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Storefront, ChartLineUp, Brain, Briefcase } from '@phosphor-icons/react';
import MarketplaceShell from '../components/MarketplaceShell';
import AgencyProfile from './AgencyProfile';
import EngagementWorkspace from './EngagementWorkspace';
import MetricsDashboard from './MetricsDashboard';
import BrandBrain from './BrandBrain';
import Engagements from './startup/screens/Engagements';

const TABS = [
  { path: '/startup/marketing',             label: 'Marketplace', icon: Storefront,  testid: 'marketing-tab-marketplace', exact: true },
  { path: '/startup/marketing/engagements', label: 'Engagements', icon: Briefcase,   testid: 'marketing-tab-engagements' },
  { path: '/startup/marketing/metrics',     label: 'Metrics',     icon: ChartLineUp, testid: 'marketing-tab-metrics' },
  { path: '/startup/marketing/brand',       label: 'Brand Brain', icon: Brain,       testid: 'marketing-tab-brand' },
];

const MarketingShell = ({ user }) => {
  const location = useLocation();

  // Hide the top-tabs when inside a focused sub-flow (engagement workspace, agency profile detail).
  const showTabs = !/\/(engagements|agency)\//.test(location.pathname);

  return (
    <div className="flex flex-col min-h-full">
      {showTabs && (
        <div className="border-b border-border bg-background sticky top-0 z-10" data-testid="marketing-shell-tabs">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-1 overflow-x-auto">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = t.exact ? location.pathname === t.path : location.pathname.startsWith(t.path);
                return (
                  <Link
                    key={t.path}
                    to={t.path}
                    data-testid={t.testid}
                    className={`relative px-4 py-3 text-sm font-medium inline-flex items-center gap-1.5 whitespace-nowrap ${active ? 'text-text-primary' : 'text-text-muted hover:text-text-secondary'}`}
                  >
                    <Icon size={13} weight="bold" /> {t.label}
                    {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-text-primary" />}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0">
        <Routes>
          <Route path="/" element={<MarketplaceShell category="agency" />} />
          <Route path="/engagements" element={<Engagements category="agency" />} />
          <Route path="/metrics" element={<MetricsDashboard user={user} />} />
          <Route path="/brand" element={<BrandBrain user={user} />} />
          <Route path="/agency/:agencyId" element={<AgencyProfile user={user} />} />
          <Route path="/engagements/:engagementId" element={<EngagementWorkspace user={user} />} />
          <Route path="*" element={<Navigate to="/startup/marketing" replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default MarketingShell;
